import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { DensityMode } from "@/lib/uiDensity";

/**
 * Per-user UI density. Deliberately NOT wrapped in redux-persist: the DB
 * (UserViewPreference, viewKey "uiDensity") is the only store, so the setting
 * follows the user to any machine they log in from. The default is
 * "comfortable" (full size — the app exactly as it was before density
 * existed), which index.html already applied inline, so only users who
 * explicitly opted into "auto"/"compact" ever see it settle after login.
 *
 * The DOM side of this lives in viewPrefSyncMiddleware (reducers stay pure).
 */
export interface UiDensityState {
  mode: DensityMode;
}

const initialState: UiDensityState = { mode: "comfortable" };

function isDensityMode(v: unknown): v is DensityMode {
  return v === "auto" || v === "compact" || v === "comfortable";
}

const uiDensitySlice = createSlice({
  name: "uiDensity",
  initialState,
  reducers: {
    /** User picked a density in Settings. Write-through saves this to the DB. */
    setUiDensityMode(state, action: PayloadAction<DensityMode>) {
      state.mode = action.payload;
    },
    /** Boot hydrate from the DB blob. Never echoes a save back. */
    hydrateUiDensity(state, action: PayloadAction<unknown>) {
      const mode = (action.payload as { mode?: unknown } | null)?.mode;
      if (isDensityMode(mode)) state.mode = mode;
    },
  },
});

export const { setUiDensityMode, hydrateUiDensity } = uiDensitySlice.actions;
export default uiDensitySlice.reducer;
